// src/components/MeetingRoomCalendarStyled.jsx
// New styled version matching Figma design while preserving all FullCalendar functionality

import React, { useEffect, useRef, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import luxon3Plugin from "@fullcalendar/luxon3";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  useTheme,
  useMediaQuery,
  Chip,
  TextField,
  Paper,
  IconButton,
  Stack,
  Divider,
} from "@mui/material";
import { styled } from '@mui/material/styles';
import { TimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
import moment from "moment-timezone";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import EditIcon from '@mui/icons-material/Edit';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EventIcon from '@mui/icons-material/Event';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';

import { getBookingsByRoom, createBooking, updateBooking } from "../services/bookingService";
import { getCurrentUser } from "../services/authService";
import { useTimezone } from "../contexts/TimezoneContext";
import { isFutureBookingInOrgTimezone } from "../utils/timezoneUtils";
import EditBookingModal from "./EditBookingModal";
import { 
  addToGoogleCalendar, 
  addToOutlookCalendar, 
  addToYahooCalendar, 
  addToAppleCalendar, 
  downloadICSFile 
} from "../utils/calendarUtils";

// Styled components with Figma design elements but preserving FullCalendar structure
const CalendarContainer = styled(Paper)(({ theme }) => ({
  marginTop: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 2,
  overflow: 'hidden',
  boxShadow: 'none',
  background: theme.palette.background.paper,
  
  // Custom FullCalendar styling with Figma design elements
  '& .fc': {
    fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
    fontSize: '16px',
  },
  
  // Custom toolbar styling to match Figma
  '& .fc-toolbar': {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    marginBottom: 0,
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderRadius: 0,
  },
  
  '& .fc-toolbar-title': {
    fontSize: '20px',
    fontWeight: 500,
    color: '#1a1a1a',
    fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
  },
  
  // Style buttons to match Figma toggle design
  '& .fc-button': {
    backgroundColor: 'transparent !important',
    border: 'none !important',
    color: '#1a1a1a !important',
    borderRadius: '11px !important',
    padding: '7px 25px !important',
    fontWeight: 400,
    fontSize: '16px !important',
    fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif !important",
    transition: 'all 0.2s ease',
    boxShadow: 'none !important',
    
    '&:hover': {
      backgroundColor: 'rgba(233, 233, 233, 0.5) !important',
      color: '#1a1a1a !important',
    },
    
    '&:focus': {
      boxShadow: 'none !important',
    },
    
    '&:disabled': {
      opacity: 0.6,
      backgroundColor: 'transparent !important',
    },
  },
  
  // Active button styling (only the selected view button gets this background)
  '& .fc-button-active': {
    backgroundColor: '#e9e9e9 !important',
    color: '#1a1a1a !important',
    fontWeight: 500,
    borderColor: 'transparent !important',
    
    '&:hover': {
      backgroundColor: '#e9e9e9 !important',
      color: '#1a1a1a !important',
    },
    
    '&:focus': {
      backgroundColor: '#e9e9e9 !important',
      color: '#1a1a1a !important',
    },
  },
  
  // Group styling for view buttons
  '& .fc-button-group': {
    '& .fc-button': {
      marginLeft: 0,
      marginRight: '8px',
      
      '&:last-child': {
        marginRight: 0,
      },
    },
  },
  
  // Today button styling
  '& .fc-today-button': {
    backgroundColor: 'transparent !important',
    borderColor: '#218ef4 !important',
    color: '#218ef4 !important',
    borderRadius: '25px !important',
    padding: '8px 16px !important',
    fontWeight: 400,
    border: '1px solid #218ef4 !important',
    transition: 'all 0.2s ease !important',
    
    '&:hover': {
      backgroundColor: '#218ef4 !important',
      borderColor: '#218ef4 !important',
      color: 'white !important',
    },
    
    '&:focus': {
      backgroundColor: '#218ef4 !important',
      borderColor: '#218ef4 !important',
      color: 'white !important',
    },
    
    '&:active': {
      backgroundColor: '#218ef4 !important',
      borderColor: '#218ef4 !important',
      color: 'white !important',
    },
  },
  
  // Navigation arrows styling
  '& .fc-prev-button, & .fc-next-button': {
    backgroundColor: 'transparent',
    border: 'none',
    color: '#1a1a1a',
    padding: '4px',
    
    '& .fc-icon': {
      fontSize: '1.2em',
    },
  },
  
  '& .fc-view-harness': {
    padding: theme.spacing(1),
  },
  
  // Column headers (days of week)
  '& .fc-col-header': {
    backgroundColor: theme.palette.background.default,
    color: '#505050',
    fontWeight: 400,
    fontSize: '16px',
    fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
    padding: theme.spacing(1),
    borderBottom: `1px solid #d9d9d9`,
  },
  
  '& .fc-col-header-cell-cushion': {
    color: '#505050',
    fontSize: '16px',
    fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
    fontWeight: 400,
  },
  
  // Date numbers styling
  '& .fc-daygrid-day-number': {
    color: '#505050',
    fontSize: '25px',
    fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
    fontWeight: 400,
  },
  
  // Time grid styling
  '& .fc-timegrid-slot': {
    borderColor: '#d9d9d9',
    height: '3em',
  },
  
  '& .fc-timegrid-slot-minor': {
    borderTopStyle: 'none',
  },
  
  // Time axis (left side time labels)
  '& .fc-timegrid-axis': {
    color: '#505050',
    fontSize: '16px',
    fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
    fontWeight: 400,
  },
  
  '& .fc-timegrid-slot-label-cushion': {
    color: '#505050',
    fontSize: '16px',
    fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
    fontWeight: 400,
  },
  
  // Vertical grid lines
  '& .fc-timegrid-col': {
    borderColor: '#d9d9d9',
  },
  
  // Events styling to match Figma
  '& .fc-event': {
    backgroundColor: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    borderRadius: '4px',
    border: 'none',
    fontSize: '12px',
    fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
    color: 'white',
    padding: '3px 7px',
    cursor: 'pointer',
    transition: theme.transitions.create(['transform', 'box-shadow']),
    
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: 'none',
    },
  },
  
  '& .fc-event-title': {
    fontWeight: 600,
    fontSize: '12px',
    lineHeight: 1.2,
  },
  
  '& .fc-event-time': {
    fontWeight: 400,
    fontSize: '12px',
    lineHeight: 1.2,
  },
  
  // Today highlighting with exact Figma colors
  '& .fc-today': {
    backgroundColor: 'rgba(33, 142, 244, 0.07) !important',
  },
  
  '& .fc-day-today': {
    backgroundColor: 'rgba(33, 142, 244, 0.07) !important',
  },
  
  '& .fc-today .fc-col-header-cell-cushion': {
    color: '#218ef4 !important',
    fontWeight: 700,
  },
  
  '& .fc-day-today .fc-col-header-cell-cushion': {
    color: '#218ef4 !important',
    fontWeight: 700,
  },
  
  // Current time indicator
  '& .fc-now-indicator': {
    borderColor: theme.palette.error.main,
    borderWidth: 2,
  },
  
  '& .fc-now-indicator-arrow': {
    borderTopColor: theme.palette.error.main,
    borderBottomColor: theme.palette.error.main,
  },
  
  // Selection highlighting
  '& .fc-select-mirror': {
    backgroundColor: 'rgba(33, 142, 244, 0.2)',
    border: '2px solid #218ef4',
    borderRadius: '4px',
  },
  
  '& .fc-highlight': {
    backgroundColor: 'rgba(33, 142, 244, 0.1)',
  },
  
  // Grid borders
  '& .fc-scrollgrid': {
    borderColor: '#d9d9d9',
  },
  
  '& .fc-scrollgrid-sync-table': {
    borderColor: '#d9d9d9',
  },
}));

// Calendar header component matching original but with Figma styling
const CalendarHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2, 3),
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
  
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: theme.spacing(1),
    alignItems: 'flex-start',
  },
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: 'none',
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  padding: theme.spacing(2, 3),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  
  '& .MuiTypography-root': {
    fontWeight: 600,
    fontSize: '1.25rem',
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 2,
  padding: theme.spacing(1, 2),
  fontWeight: 500,
  textTransform: 'none',
  transition: theme.transitions.create(['transform', 'box-shadow']),
  
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: 'none',
  },
}));

const InfoChip = styled(Chip)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 3,
  backgroundColor: `${theme.palette.primary.main}10`,
  color: theme.palette.primary.main,
  border: `1px solid ${theme.palette.primary.main}30`,
  fontWeight: 500,
  fontSize: '0.75rem',
  
  '& .MuiChip-icon': {
    color: theme.palette.primary.main,
  },
}));

// Helper functions from original component
function isDateInPast(date, orgTimeZone) {
  const now = moment.tz(orgTimeZone);
  const dateInOrgTz = moment.tz(date, orgTimeZone);
  return dateInOrgTz.isBefore(now);
}

function hasOverlap(bookings, startSelected, endSelected, orgTimeZone) {
  const selectedStartInOrgTz = moment.tz(startSelected, orgTimeZone);
  const selectedEndInOrgTz = moment.tz(endSelected, orgTimeZone);
  const startMs = selectedStartInOrgTz.valueOf();
  const endMs = selectedEndInOrgTz.valueOf();

  return bookings.some((b) => {
    const bStartInOrgTz = moment.tz(b.startTime, orgTimeZone);
    const bEndInOrgTz = moment.tz(b.endTime, orgTimeZone);
    const selectedInOrgTz = moment.tz(startSelected, orgTimeZone);
    
    const bStartMs = bStartInOrgTz.valueOf();
    const bEndMs = bEndInOrgTz.valueOf();

    if (bStartInOrgTz.isSame(selectedInOrgTz, 'day')) {
      if (!(endMs <= bStartMs || startMs >= bEndMs)) {
        return true;
      }
    }
    return false;
  });
}

function MeetingRoomCalendarStyled({
  roomId,
  refreshKey,
  chosenDate,
}) {
  const calendarRef = useRef(null);
  const { enqueueSnackbar } = useSnackbar();
  const { orgTimeZone, loading: timezoneLoading } = useTimezone();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));
  
  // Get user from Redux store
  const user = useSelector((state) => state.user.user);
  
  // Current view state
  const [currentView, setCurrentView] = useState('timeGridWeek');
  const [currentDate, setCurrentDate] = useState(moment());
  
  // org default times - initialize with organization settings if available
  const [orgStart, setOrgStart] = useState(
    user?.currentOrganization?.defaultStartTime || "08:00"
  );
  const [orgEnd, setOrgEnd] = useState(
    user?.currentOrganization?.defaultEndTime || "18:00"
  );

  // slotMinTime/slotMaxTime for FullCalendar
  const [slotMinTime, setSlotMinTime] = useState(
    user?.currentOrganization?.defaultStartTime ? 
    `${user.currentOrganization.defaultStartTime}:00` : 
    "08:00:00"
  );
  const [slotMaxTime, setSlotMaxTime] = useState(
    user?.currentOrganization?.defaultEndTime ? 
    `${user.currentOrganization.defaultEndTime}:00` : 
    "18:00:00"
  );

  const [userId, setUserId] = useState(null);
  const [bookings, setBookings] = useState([]);

  // Dialog states (same as original)
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRange, setSelectedRange] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [selectedBookingForAction, setSelectedBookingForAction] = useState(null);
  const [calendarDialogOpen, setCalendarDialogOpen] = useState(false);
  const [selectedBookingForCalendar, setSelectedBookingForCalendar] = useState(null);

  // Load user settings (same as original)
  useEffect(() => {
    if (user && user.currentOrganization) {
      const startTime = user.currentOrganization.defaultStartTime || "08:00";
      const endTime = user.currentOrganization.defaultEndTime || "18:00";
      
      setOrgStart(startTime);
      setOrgEnd(endTime);
      setSlotMinTime(`${startTime}:00`);
      setSlotMaxTime(`${endTime}:00`);
      
      if (user._id) {
        setUserId(user._id);
      }
      
      // Force calendar to update view when times change
      setTimeout(() => {
        if (calendarRef.current) {
          const calendarApi = calendarRef.current.getApi();
          calendarApi.render();
        }
      }, 100);
    }
  }, [user]);

  // Fetch existing bookings (same as original)
  async function fetchBookings() {
    try {
      const data = await getBookingsByRoom(roomId);
      const mapped = data.map((b) => {
        const isMyBooking = b.user?._id === userId;
        return {
          id: b._id,
          title: b.meetingName || (isMyBooking ? `My Booking` : `Booked by ${b.user?.username || "[Unknown]"}`),
          start: new Date(b.startTime),
          end: new Date(b.endTime),
          startTime: b.startTime,
          endTime: b.endTime,
          booking: b,
          isMyBooking: isMyBooking,
          backgroundColor: muiTheme.palette.primary.main,
          borderColor: muiTheme.palette.primary.main,
          textColor: 'white',
          editable: isMyBooking,
          extendedProps: {
            isMyBooking: isMyBooking,
            booking: b
          }
        };
      });
      setBookings(mapped);

      if (calendarRef.current) {
        const api = calendarRef.current.getApi();
        api.removeAllEvents();
        api.addEventSource(mapped);
      }
    } catch (err) {
      console.error("Failed to load bookings:", err);
    }
  }

  useEffect(() => {
    if (userId) {
      fetchBookings();
    }
  }, [roomId, refreshKey, userId]);

  // If chosenDate changes => gotoDate
  useEffect(() => {
    if (calendarRef.current && chosenDate) {
      const api = calendarRef.current.getApi();
      api.gotoDate(chosenDate);
    }
  }, [chosenDate]);

  // Event handlers (same as original)
  function handleSelect(selectInfo) {
    const { start, end } = selectInfo;

    if (isDateInPast(start, orgTimeZone)) {
      enqueueSnackbar("Cannot book in the past", { variant: "error" });
      return;
    }

    setSelectedRange({
      start,
      end,
      allDay: selectInfo.allDay,
    });
    setStartTime(start);
    setEndTime(end);
    setDialogOpen(true);
  }

  function closeDialog() {
    setDialogOpen(false);
    setSelectedRange(null);
    setStartTime(null);
    setEndTime(null);
  }

  function handleEventClick(clickInfo) {
    const event = clickInfo.event;
    const isMyBooking = event.extendedProps.isMyBooking;
    const booking = event.extendedProps.booking;
    
    if (isMyBooking) {
      // Check if booking is in the past
      if (!isFutureBookingInOrgTimezone(booking.endTime, orgTimeZone)) {
        enqueueSnackbar("Cannot edit past bookings", { variant: "warning" });
        return;
      }
      
      setSelectedBookingForAction(booking);
      setActionDialogOpen(true);
    } else {
      enqueueSnackbar("This booking belongs to another user", { variant: "info" });
    }
  }

  async function handleEditBooking(bookingId, { startTime, endTime, meetingName }) {
    try {
      await updateBooking(bookingId, { startTime, endTime, meetingName });
      enqueueSnackbar("Booking updated successfully", { variant: "success" });
      fetchBookings();
    } catch (error) {
      console.error("Error updating booking:", error);
      throw error;
    }
  }

  async function handleEventDrop(dropInfo) {
    const event = dropInfo.event;
    const isMyBooking = event.extendedProps.isMyBooking;
    
    if (!isMyBooking) {
      enqueueSnackbar("You can only move your own bookings", { variant: "error" });
      dropInfo.revert();
      return;
    }

    if (isDateInPast(event.start, orgTimeZone)) {
      enqueueSnackbar("Cannot move booking to the past", { variant: "error" });
      dropInfo.revert();
      return;
    }

    const otherBookings = bookings.filter(b => b.id !== event.id);
    if (hasOverlap(otherBookings, event.start, event.end, orgTimeZone)) {
      enqueueSnackbar("Time conflicts with another booking", { variant: "error" });
      dropInfo.revert();
      return;
    }

    try {
      await updateBooking(event.extendedProps.booking._id, {
        startTime: event.start.toISOString(),
        endTime: event.end.toISOString(),
        meetingName: event.extendedProps.booking.meetingName,
      });
      enqueueSnackbar("Booking moved successfully", { variant: "success" });
      fetchBookings();
    } catch (error) {
      console.error("Error moving booking:", error);
      enqueueSnackbar("Error moving booking", { variant: "error" });
      dropInfo.revert();
    }
  }

  async function handleEventResize(resizeInfo) {
    const event = resizeInfo.event;
    const isMyBooking = event.extendedProps.isMyBooking;
    
    if (!isMyBooking) {
      enqueueSnackbar("You can only resize your own bookings", { variant: "error" });
      resizeInfo.revert();
      return;
    }

    if (isDateInPast(event.start, orgTimeZone)) {
      enqueueSnackbar("Cannot resize booking to the past", { variant: "error" });
      resizeInfo.revert();
      return;
    }

    const otherBookings = bookings.filter(b => b.id !== event.id);
    if (hasOverlap(otherBookings, event.start, event.end, orgTimeZone)) {
      enqueueSnackbar("Time conflicts with another booking", { variant: "error" });
      resizeInfo.revert();
      return;
    }

    try {
      await updateBooking(event.extendedProps.booking._id, {
        startTime: event.start.toISOString(),
        endTime: event.end.toISOString(),
        meetingName: event.extendedProps.booking.meetingName,
      });
      enqueueSnackbar("Booking resized successfully", { variant: "success" });
      fetchBookings();
    } catch (error) {
      console.error("Error resizing booking:", error);
      enqueueSnackbar("Error resizing booking", { variant: "error" });
      resizeInfo.revert();
    }
  }

  async function handleConfirmBooking() {
    if (!startTime || !endTime) {
      enqueueSnackbar("Please select both start and end times", { variant: "error" });
      return;
    }

    if (isDateInPast(startTime, orgTimeZone)) {
      enqueueSnackbar("Cannot book in the past", { variant: "error" });
      return;
    }

    const startInOrgTz = moment.tz(startTime, orgTimeZone);
    const endInOrgTz = moment.tz(endTime, orgTimeZone);
    const [orgSh, orgSm] = orgStart.split(":").map(Number);
    const [orgEh, orgEm] = orgEnd.split(":").map(Number);
    
    const orgMin = startInOrgTz.clone().set({ hour: orgSh, minute: orgSm, second: 0 });
    const orgMax = startInOrgTz.clone().set({ hour: orgEh, minute: orgEm, second: 0 });
    
    if (startInOrgTz.isBefore(orgMin) || endInOrgTz.isAfter(orgMax)) {
      enqueueSnackbar(
        `Time must be within organization's allowed range: ${orgStart} - ${orgEnd} (${orgTimeZone})`,
        { variant: "error" }
      );
      return;
    }

    if (hasOverlap(bookings, startTime, endTime, orgTimeZone)) {
      enqueueSnackbar("This time range is already booked", { variant: "error" });
      return;
    }

    try {
      await createBooking({
        roomId,
        userId,
        startTime,
        endTime,
      });
      enqueueSnackbar("Booking created successfully", { variant: "success" });
      fetchBookings();
      closeDialog();
    } catch (err) {
      console.error("Booking error:", err);
      enqueueSnackbar("Error creating booking", { variant: "error" });
    }
  }

  // Dialog handlers (same as original)
  function closeEditModal() {
    setEditModalOpen(false);
    setSelectedBooking(null);
  }

  function closeActionDialog() {
    setActionDialogOpen(false);
    setSelectedBookingForAction(null);
  }

  function handleEditBookingAction() {
    setSelectedBooking(selectedBookingForAction);
    setEditModalOpen(true);
    closeActionDialog();
  }

  function handleAddToCalendarAction() {
    setSelectedBookingForCalendar(selectedBookingForAction);
    setCalendarDialogOpen(true);
    closeActionDialog();
  }

  function closeCalendarDialog() {
    setCalendarDialogOpen(false);
    setSelectedBookingForCalendar(null);
  }

  // Calendar integration handlers (same as original)
  function handleAddToGoogleCalendar() {
    if (selectedBookingForCalendar) {
      try {
        addToGoogleCalendar(selectedBookingForCalendar, orgTimeZone);
        closeCalendarDialog();
      } catch (error) {
        console.error("Error adding to Google Calendar:", error);
      }
    }
  }

  function handleAddToOutlookCalendar() {
    if (selectedBookingForCalendar) {
      try {
        addToOutlookCalendar(selectedBookingForCalendar, orgTimeZone);
        closeCalendarDialog();
      } catch (error) {
        console.error("Error adding to Outlook Calendar:", error);
      }
    }
  }

  function handleAddToYahooCalendar() {
    if (selectedBookingForCalendar) {
      try {
        addToYahooCalendar(selectedBookingForCalendar, orgTimeZone);
        closeCalendarDialog();
      } catch (error) {
        console.error("Error adding to Yahoo Calendar:", error);
      }
    }
  }

  function handleAddToAppleCalendar() {
    if (selectedBookingForCalendar) {
      try {
        addToAppleCalendar(selectedBookingForCalendar, orgTimeZone);
        closeCalendarDialog();
      } catch (error) {
        console.error("Error adding to Apple Calendar:", error);
      }
    }
  }

  function handleDownloadICS() {
    if (selectedBookingForCalendar) {
      try {
        downloadICSFile(selectedBookingForCalendar, orgTimeZone);
        closeCalendarDialog();
      } catch (error) {
        console.error("Error downloading ICS file:", error);
      }
    }
  }

  // FullCalendar config
  const initialView = isMobile ? "timeGridDay" : "timeGridWeek";
  const headerToolbar = isMobile
    ? { left: "prev,next", center: "title", right: "" }
    : {
        left: "prev,next today",
        center: "title",
        right: "dayGridMonth,timeGridWeek,timeGridDay",
      };
  const aspectRatioValue = isMobile ? 0.8 : 1.35;

  return (
    <CalendarContainer elevation={0}>
      
      <FullCalendar
        ref={calendarRef}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, luxon3Plugin]}
        initialView={initialView}
        headerToolbar={headerToolbar}
        timeZone={orgTimeZone}
        slotMinTime={slotMinTime}
        slotMaxTime={slotMaxTime}
        slotDuration="00:30:00"
        slotLabelInterval="01:00:00"
        slotLabelFormat={{
          hour: 'numeric',
          minute: '2-digit',
          omitZeroMinute: false,
          meridiem: 'short'
        }}
        businessHours={{
          daysOfWeek: [0, 1, 2, 3, 4, 5, 6], // All days of the week
          startTime: orgStart,
          endTime: orgEnd,
        }}
        selectConstraint="businessHours"
        eventConstraint="businessHours"
        selectable={true}
        selectMirror={true}
        select={handleSelect}
        eventClick={handleEventClick}
        editable={true}
        eventDrop={handleEventDrop}
        eventResize={handleEventResize}
        longPressDelay={200}
        selectLongPressDelay={200}
        nowIndicator={true}
        height="auto"
        aspectRatio={aspectRatioValue}
        events={[]}
        allDaySlot={false}
        key={`${orgStart}-${orgEnd}`} // Force re-render when org times change
      />

      {/* Booking Confirmation Dialog */}
      <StyledDialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <StyledDialogTitle>
          <CalendarTodayIcon />
          <Typography variant="h6">Confirm Booking</Typography>
        </StyledDialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {selectedRange && (
            <Stack spacing={3}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Adjust start/end times if needed:
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <TimePicker
                    label="Start Time"
                    ampm={false}
                    value={startTime}
                    onChange={(v) => setStartTime(v)}
                    slots={{
                      textField: (params) => <TextField {...params} fullWidth />
                    }}
                  />
                  <TimePicker
                    label="End Time"
                    ampm={false}
                    value={endTime}
                    onChange={(v) => setEndTime(v)}
                    slots={{
                      textField: (params) => <TextField {...params} fullWidth />
                    }}
                  />
                </LocalizationProvider>
              </Box>

              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                  Booking Details
                </Typography>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarTodayIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      <strong>Date:</strong> {startTime ? moment.tz(startTime, orgTimeZone).format("MMM DD, YYYY") : "--"}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccessTimeIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      <strong>Time:</strong> {startTime && endTime 
                        ? `${moment.tz(startTime, orgTimeZone).format("HH:mm")} - ${moment.tz(endTime, orgTimeZone).format("HH:mm")}`
                        : "--"}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationOnIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      <strong>Timezone:</strong> {orgTimeZone}
                    </Typography>
                  </Box>
                </Stack>
                
                <Divider sx={{ my: 1 }} />
                
                <Typography variant="body2" color="text.secondary">
                  Allowed hours: {orgStart} - {orgEnd}
                </Typography>
              </Paper>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <ActionButton onClick={closeDialog} variant="outlined">
            Cancel
          </ActionButton>
          <ActionButton 
            variant="contained" 
            onClick={handleConfirmBooking}
            startIcon={<CalendarTodayIcon />}
          >
            Confirm Booking
          </ActionButton>
        </DialogActions>
      </StyledDialog>

      {/* Edit Booking Modal */}
      <EditBookingModal
        open={editModalOpen}
        onClose={closeEditModal}
        booking={selectedBooking}
        onSave={handleEditBooking}
        organizationWorkHours={{ start: orgStart, end: orgEnd }}
      />

      {/* Action Selection Dialog */}
      <StyledDialog open={actionDialogOpen} onClose={closeActionDialog} maxWidth="sm" fullWidth>
        <StyledDialogTitle>
          <EventIcon />
          <Typography variant="h6">Booking Actions</Typography>
        </StyledDialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {selectedBookingForAction && (
            <Stack spacing={3}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                  Booking Information
                </Typography>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationOnIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      <strong>Room:</strong> {selectedBookingForAction.room?.name || 'Meeting Room'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarTodayIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      <strong>Date:</strong> {moment.tz(selectedBookingForAction.startTime, orgTimeZone).format('MMM DD, YYYY')}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccessTimeIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      <strong>Time:</strong> {moment.tz(selectedBookingForAction.startTime, orgTimeZone).format('h:mm A')} - {moment.tz(selectedBookingForAction.endTime, orgTimeZone).format('h:mm A')}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
              
              <Stack spacing={2}>
                <ActionButton 
                  variant="contained" 
                  onClick={handleEditBookingAction}
                  startIcon={<EditIcon />}
                  fullWidth
                >
                  Edit Booking
                </ActionButton>
                <ActionButton 
                  variant="outlined" 
                  onClick={handleAddToCalendarAction}
                  startIcon={<CalendarTodayIcon />}
                  fullWidth
                >
                  Add to Calendar
                </ActionButton>
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <ActionButton onClick={closeActionDialog} variant="outlined">
            Close
          </ActionButton>
        </DialogActions>
      </StyledDialog>

      {/* Calendar Integration Dialog */}
      <StyledDialog open={calendarDialogOpen} onClose={closeCalendarDialog} maxWidth="sm" fullWidth>
        <StyledDialogTitle>
          <CalendarTodayIcon />
          <Typography variant="h6">Add to Calendar</Typography>
        </StyledDialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {selectedBookingForCalendar && (
            <Stack spacing={3}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Choose your preferred calendar application:
              </Typography>
              
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                  Event Details
                </Typography>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationOnIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      <strong>Room:</strong> {selectedBookingForCalendar.room?.name || 'Meeting Room'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarTodayIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      <strong>Date:</strong> {moment.tz(selectedBookingForCalendar.startTime, orgTimeZone).format('MMM DD, YYYY')}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccessTimeIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      <strong>Time:</strong> {moment.tz(selectedBookingForCalendar.startTime, orgTimeZone).format('h:mm A')} - {moment.tz(selectedBookingForCalendar.endTime, orgTimeZone).format('h:mm A')}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
              
              <Stack spacing={2}>
                <ActionButton variant="outlined" onClick={handleAddToGoogleCalendar} fullWidth>
                  📅 Google Calendar
                </ActionButton>
                <ActionButton variant="outlined" onClick={handleAddToOutlookCalendar} fullWidth>
                  📘 Outlook Calendar
                </ActionButton>
                <ActionButton variant="outlined" onClick={handleAddToYahooCalendar} fullWidth>
                  🟣 Yahoo Calendar
                </ActionButton>
                <ActionButton variant="outlined" onClick={handleAddToAppleCalendar} fullWidth>
                  🍎 Apple Calendar
                </ActionButton>
                <ActionButton variant="outlined" onClick={handleDownloadICS} fullWidth>
                  📄 Download .ics file
                </ActionButton>
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <ActionButton onClick={closeCalendarDialog} variant="outlined">
            Close
          </ActionButton>
        </DialogActions>
      </StyledDialog>
    </CalendarContainer>
  );
}

export default MeetingRoomCalendarStyled;